// src/area/SorteoArea/SorteoArea.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../config/config.js';
import toast from 'react-hot-toast';

// Orden fijo de regiones / abreviaturas
const REGIONS = [
    'L. Norte 3',
    'L. Norte 2',
    'L. Norte 1',
    'L. Norte Chico',
    'Oficina 1',
    'Oficina 2',
    'Stand Alone',
    'L. Sur 1',
    'L. Sur 2',
    'L. Sur Chico',
    'L. Centro 1',
    'Z.L. Este',
    'Z.L. Centro',
];

// Mezcla aleatoria
const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

const SorteoArea = () => {
    const [participants, setParticipants] = useState([]);
    const [pairs, setPairs] = useState([]);
    const [loadingSave, setLoadingSave] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [error, setError] = useState('');

    // Fetch inicial
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.post(`${API_BASE_URL}/getparticipants`, {
                    idevent: 16,
                    idsport: 5,
                    idtypephase: 1,
                });
                setParticipants(data);
                // Construir pares en orden fijo
                const initial = REGIONS.map((region) => {
                    const p = data.find(
                        (x) => x.Institution.abrev.toLowerCase() === region.toLowerCase()
                    );
                    return {
                        idinstitution: p?.Institution.idinstitution || null,
                        abrev: region,
                        name: p?.Institution.name || '🔒',
                        logo: p
                            ? p.Institution.path_base + p.Institution.image_path
                            : 'https://storage.googleapis.com/la_liga_files/fedup/images/logo_primax.png',
                    };
                });
                setPairs(initial);
            } catch (e) {
                console.error(e);
                setError('No se pudieron cargar los participantes');
            }
        })();
    }, []);

    // Sortear clubs
    const handleSorteo = () => {
        if (participants.length !== REGIONS.length) {
            setError('La cantidad de participantes no coincide con las regiones.');
            return;
        }
        const shuffled = shuffle(participants);
        const newPairs = REGIONS.map((region, i) => {
            const p = shuffled[i];
            return {
                idinstitution: p.Institution.idinstitution,
                abrev: region,
                name: p.Institution.name,
                logo: p.Institution.path_base + p.Institution.image_path,
            };
        });
        setPairs(newPairs);
        setIsDirty(true);
        setError('');
    };

    // Guardar cambios
    const handleGuardar = async () => {
        if (!isDirty) return;
        setLoadingSave(true);
        try {
            const payload = pairs.map(({ idinstitution, abrev }) => ({ idinstitution, abrev }));
            await axios.patch(`${API_BASE_URL}/updateinstitutions`, payload);
            setIsDirty(false);
            toast.success('Actualización exitosa');
        } catch (e) {
            console.error(e);
            toast.error(e.response?.data?.msg || 'Error al guardar');
        } finally {
            setLoadingSave(false);
        }
    };

    return (
        <div className="p-6 bg-gray-100 min-h-screen">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">
                Sorteo Instituciones – Clubs
            </h2>

            <div className="flex gap-4 mb-8">
                <button
                    onClick={handleSorteo}
                    className="px-6 py-3 bg-blue-600 text-white rounded-2xl shadow-lg hover:bg-blue-700 transition"
                >
                    Realizar Sorteo
                </button>

                <button
                    onClick={handleGuardar}
                    disabled={!isDirty || loadingSave}
                    className={`px-6 py-3 flex items-center rounded-2xl shadow-lg transition ${isDirty
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        }`}
                >
                    {loadingSave && (
                        <svg
                            className="animate-spin h-5 w-5 mr-2"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            />
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4l4-4-4-4v4a12 12 0 00-12 12h4z"
                            />
                        </svg>
                    )}
                    {loadingSave ? 'Guardando…' : 'Guardar Cambios'}
                </button>
            </div>

            {error && <p className="text-red-600 font-semibold mb-4">{error}</p>}

            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
                {pairs.map(({ abrev, name, logo }, idx) => (
                    <div
                        key={idx}
                        className="flex items-center justify-between bg-white p-4 rounded-2xl shadow-md hover:shadow-xl transition"
                    >
                        <span className="text-lg font-medium text-gray-700">{abrev}</span>
                        <div className="flex items-center space-x-3">
                            <img
                                src={logo}
                                alt={name}
                                className="w-12 h-12 rounded-full object-cover"
                            />
                            <span className="text-sm font-semibold text-gray-800">{name}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SorteoArea;
